import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from 'lib/api';

export const Operations = (queryKey, options = {}) => {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [expandedReplies, setExpandedReplies] = useState({});
  const { onReplySuccess, threadId } = options;
  const sortBy = queryKey.length > 2 ? queryKey[2] : 'newest';

  const fetchNestedReplies = async (replyId) => {
    try {
      return await api.twut.listThread(replyId, sortBy);
    } catch (error) {
      console.error('Error fetching nested replies:', error);
      return null;
    }
  };

  const handleExpandReplies = async (replyId) => {
    if (expandedReplies[replyId]) {
      const childIds = [];
      const findAllChildren = (replies, parentId) => {
        if (!replies) return;

        replies.forEach(reply => {
          childIds.push(reply.id);
          if (expandedReplies[reply.id]) {
            findAllChildren(expandedReplies[reply.id].replies, reply.id);
          }
        });
      };

      if (expandedReplies[replyId] && expandedReplies[replyId].replies) {
        findAllChildren(expandedReplies[replyId].replies, replyId);
      }

      const newExpandedReplies = { ...expandedReplies };
      delete newExpandedReplies[replyId];

      childIds.forEach(id => {
        delete newExpandedReplies[id];
      });

      setExpandedReplies(newExpandedReplies);
      return;
    }

    const refreshedReplies = await fetchNestedReplies(replyId);
    setExpandedReplies(prev => ({
      ...prev,
      [replyId]: refreshedReplies
    }));
  };

  const refreshThreadAndExpanded = (justAddedToParentId = null) => {
    setTimeout(() => {
      queryClient.invalidateQueries(queryKey).then(async () => {
        if (justAddedToParentId) {
          const refreshedReplies = await fetchNestedReplies(justAddedToParentId);

          setExpandedReplies(prev => ({
            ...prev,
            [justAddedToParentId]: refreshedReplies
          }));

          const parentMap = {};

          Object.entries(expandedReplies).forEach(([replyId, data]) => {
            if (data && data.replies) {
              data.replies.forEach(reply => {
                parentMap[reply.id] = replyId;
              });
            }
          });

          let currentId = justAddedToParentId;
          while (parentMap[currentId]) {
            const parentId = parentMap[currentId];
            const refreshedParent = await fetchNestedReplies(parentId);

            setExpandedReplies(prev => ({
              ...prev,
              [parentId]: refreshedParent
            }));

            currentId = parentId;
          }

          return;
        }

        if (Object.keys(expandedReplies).length === 0) return;

        const refreshPromises = Object.keys(expandedReplies).map(async (id) => {
          return { id, data: await fetchNestedReplies(id) };
        });

        Promise.all(refreshPromises).then(results => {
          const newExpandedState = {};
          results.forEach(({ id, data }) => {
            newExpandedState[id] = data;
          });
          setExpandedReplies(newExpandedState);
        });
      });
    }, 300);
  };

  const createTwutMutation = useMutation({
    mutationFn: (data) => api.twut.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['twutsList']);
      setToast({
        show: true,
        message: 'Twut posted successfully',
        type: 'success'
      });
    },
    onError: (error) => {
      setToast({
        show: true,
        message: error.response?.data?.message || 'Failed to create twut',
        type: 'danger'
      });
    }
  });

  const replyTwutMutation = useMutation({
    mutationFn: ({ parentId, data }) => api.twut.reply(parentId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(queryKey);
      queryClient.invalidateQueries(['twutThread', variables.parentId]);

      setToast({
        show: true,
        message: 'Reply posted successfully',
        type: 'success'
      });

      if (threadId) {
        refreshThreadAndExpanded(variables.parentId);
      }

      if (onReplySuccess) {
        onReplySuccess(variables.parentId);
      }
    },
    onError: (error) => {
      setToast({
        show: true,
        message: error.response?.data?.message || 'Failed to post reply',
        type: 'danger'
      });
    }
  });

  const updateTwutMutation = useMutation({
    mutationFn: ({ id, data }) => api.twut.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(queryKey);

      if (threadId) {
        refreshThreadAndExpanded();
      }

      setToast({
        show: true,
        message: 'Twut updated successfully',
        type: 'success'
      });
    },
    onError: (error) => {
      setToast({
        show: true,
        message: error.response?.data?.message || 'Failed to update twut',
        type: 'danger'
      });
    }
  });

  const deleteTwutMutation = useMutation({
    mutationFn: api.twut.delete,
    onMutate: async (id) => {
      await queryClient.cancelQueries(queryKey);

      const previousData = queryClient.getQueryData(queryKey);
      const previousExpandedReplies = { ...expandedReplies };

      if (previousData) {
        if (Array.isArray(previousData)) {
          queryClient.setQueryData(queryKey,
            previousData.filter(twut => String(twut.id) !== String(id))
          );
        }
        else if (previousData.replies) {
          const removeFromReplies = (replies) => {
            if (!replies) return [];
            const filtered = replies.filter(r => String(r.id) !== String(id));
            return filtered.map(r => ({
              ...r,
              replies: r.replies ? removeFromReplies(r.replies) : []
            }));
          };

          if (String(id) !== String(previousData.id)) {
            queryClient.setQueryData(queryKey, {
              ...previousData,
              replies: removeFromReplies(previousData.replies)
            });
          }
        }
      }

      const newExpandedReplies = { ...expandedReplies };

      const removeFromExpandedReplies = (parentId) => {
        if (!newExpandedReplies[parentId]) return;

        const parent = newExpandedReplies[parentId];
        if (parent && parent.replies) {
          parent.replies = parent.replies.filter(r => String(r.id) !== String(id));
          parent.replies.forEach(reply => removeFromExpandedReplies(reply.id));
          newExpandedReplies[parentId] = parent;
        }
      };

      if (threadId) {
        removeFromExpandedReplies(threadId);
      }

      Object.keys(newExpandedReplies).forEach(replyId => {
        removeFromExpandedReplies(replyId);
      });

      setExpandedReplies(newExpandedReplies);

      return { previousData, previousExpandedReplies };
    },
    onError: (err, id, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }

      if (context?.previousExpandedReplies) {
        setExpandedReplies(context.previousExpandedReplies);
      }

      setToast({
        show: true,
        message: 'Failed to delete twut',
        type: 'danger'
      });
    },
    onSuccess: (_, id) => {
      if (threadId && String(id) === String(threadId) && options.navigateHome) {
        options.navigateHome();
      } else {
        queryClient.invalidateQueries(queryKey);

        refreshThreadAndExpanded();
      }

      setToast({
        show: true,
        message: 'Twut deleted successfully',
        type: 'success'
      });
    }
  });

  const handleReplySubmission = async (parentId, data, getReplyDepth = null) => {
    await replyTwutMutation.mutateAsync({ parentId, data });

    if (getReplyDepth) {
      const parentDepth = getReplyDepth(parentId);

      if (parentDepth >= 2 && options.navigateToThread) {
        options.navigateToThread(parentId);
        return;
      }
    }

    if (threadId) {
      const refreshedReplies = await fetchNestedReplies(parentId);
      setExpandedReplies(prev => ({
        ...prev,
        [parentId]: refreshedReplies
      }));

      refreshThreadAndExpanded(parentId);
    }
  };

  return {
    toast,
    clearToast: () => setToast(prev => ({ ...prev, show: false })),
    setToast,
    createTwut: createTwutMutation.mutate,
    replyTwut: (parentId, data) => replyTwutMutation.mutate({ parentId, data }),
    updateTwut: (id, data) => updateTwutMutation.mutate({ id, data }),
    deleteTwut: deleteTwutMutation.mutate,

    expandedReplies,
    handleExpandReplies,
    refreshThreadAndExpanded,
    fetchNestedReplies,
    handleReplySubmission,

    isProcessing: createTwutMutation.isPending ||
      replyTwutMutation.isPending ||
      updateTwutMutation.isPending ||
      deleteTwutMutation.isPending
  };
};
